import React, { useState, useMemo, useEffect } from 'react'
import { Link } from 'react-router-dom'
import sha1 from 'sha1'
import { motion } from 'framer-motion'
import {
  ArrowLeftIcon,
  UserIcon,
  LockClosedIcon,
  PhotographIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  LogoutIcon,
  ChevronRightIcon,
  HomeIcon,
  SaveIcon,
  BellIcon
} from '@heroicons/react/outline'
import PushSubscriptionToggle from '../UI/PushSubscriptionToggle'
import connexion from '../../connexion'
import tools from '../../tools'

const inputClass = 'browser-default w-full border border-slate-200 rounded-lg px-4 py-3 text-sm bg-slate-50/50 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all'
const labelClass = 'block text-[10px] font-bold text-slate-400 mb-1.5 mt-2 uppercase tracking-widest italic'
const cardClass = 'bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden'
const cardHeaderClass = 'px-5 py-4 border-b border-slate-100 flex items-center bg-slate-50/30'

function User({ user, updateUser, updateNavbarTitle, onBack, onLogout, establishment,
  sidebarCollapsed, onToggleSidebar, history, location,
  fetchServiceProductsByService, insertServiceProduct, updateServiceProduct, deleteServiceProduct
}) {
  const [form, setForm] = useState({
    password: '',
    avatar: user ? user.avatar : null,
    lastname: user ? (user.lastname || '') : '',
    firstname: user ? (user.firstname || '') : '',
    email: user ? (user.email || '') : '',
    username: user ? (user.username || '') : '',
    newpassword: '',
    confirm: ''
  })
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const fromInterventions = location && location.state && location.state.from === '/interventions'

  useEffect(() => {
    if (updateNavbarTitle)
      updateNavbarTitle('Paramètres')
  }, [])

  const initials = useMemo(() => {
    const first = (form.firstname || '').trim()
    const last = (form.lastname || '').trim()
    if (first && last)
      return (first[0] + last[0]).toUpperCase()
    if (first)
      return first.slice(0, 2).toUpperCase()
    return '?'
  }, [form.firstname, form.lastname])

  const fullName = useMemo(() => {
    return ((form.firstname || '') + ' ' + (form.lastname || '')).trim() || '—'
  }, [form.firstname, form.lastname])

  const handleField = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value })
    setError(null)
    setSuccess(null)
  }

  const handleAvatar = (e) => {
    const reader = new FileReader()
    const file = e.target.files[0]
    reader.addEventListener('load', () => {
      setForm({ ...form, avatar: reader.result })
    }, false)
    if (file)
      reader.readAsDataURL(file)
  }

  const handleSettings = (e) => {
    if (e && e.preventDefault)
      e.preventDefault()
    const { password, avatar, lastname, firstname, email, username, newpassword, confirm } = form
    if (sha1(password) !== user.password)
    {
      setError('Mot de passe actuel incorrect — vérifiez le champ "Mot de passe actuel"')
      setSuccess(null)
    }
    else if (lastname.length === 0)
    {
      setError('Veuillez indiquer le nom')
      setSuccess(null)
    }
    else if (firstname.length === 0)
    {
      setError('Veuillez indiquer le prénom')
      setSuccess(null)
    }
    else if (email.length === 0)
    {
      setError("Veuillez indiquer l'adresse e-mail")
      setSuccess(null)
    }
    else if (tools.testEmail(email) === false)
    {
      setError('Adresse e-mail invalide')
      setSuccess(null)
    }
    else if (username.length === 0)
    {
      setError("Veuillez indiquer le nom d'utilisateur")
      setSuccess(null)
    }
    else if (username.length < 4)
    {
      setError("Nom d'utilisateur trop court (4 caractères min.)")
      setSuccess(null)
    }
    else if (newpassword.length > 0 && newpassword.length < 8)
    {
      setError('Mot de passe trop court (8 caractères min.)')
      setSuccess(null)
    }
    else if (newpassword.length > 0 && newpassword !== confirm)
    {
      setError('Le mot de passe et sa confirmation sont différents')
      setSuccess(null)
    }
    else
    {
      fetch(connexion.connect.url + 'dashboard/users/update', {
        method: 'POST',
        body: JSON.stringify({
          id: user._id,
          lastname,
          firstname,
          email,
          username,
          password: newpassword,
          avatar
        })
      })
      .then(res => res.json())
      .then(ret => {
        if (ret === false)
        {
          setError("Une erreur s'est produite")
          setSuccess(null)
        }
        else if (ret === 1)
        {
          setError('Un autre utilisateur avec le même identifiant existe déjà')
          setSuccess(null)
        }
        else
        {
          setForm({
            password: '',
            avatar: ret.avatar,
            lastname: ret.lastname,
            firstname: ret.firstname,
            email: ret.email,
            username: ret.username,
            newpassword: '',
            confirm: ''
          })
          if (updateUser)
            updateUser(ret, true)
          setError(null)
          setSuccess('Les paramètres ont bien été mis à jour')
        }
      })
      .catch(err => {
        setError("Une erreur s'est produite")
        setSuccess(null)
        console.log(err)
      })
    }
  }

  const handleLogout = () => {
    setShowLogoutConfirm(false)
    if (onLogout)
      onLogout()
    else
      updateUser(null)
  }

  return (
    <div className="flex flex-col bg-slate-50" style={{ height: '100vh', overflow: 'hidden' }}>

      {/* ── HEADER MOBILE / TABLETTE ── */}
      <header className="lg:hidden px-6 pt-12 pb-5 border-b border-slate-100 bg-white flex items-center gap-4 shrink-0 z-10 shadow-sm">
        {sidebarCollapsed && onToggleSidebar && (
          <button
            onClick={onToggleSidebar}
            className="hidden lg:flex w-9 h-9 rounded-lg bg-gray-800 hover:bg-gray-700 text-white items-center justify-center transition-colors shrink-0"
            title="Ouvrir le menu"
            style={{ backgroundImage: 'none', backgroundColor: '#1f2937' }}
          >
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        )}
        {onBack && (
          <button
            onClick={onBack}
            className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center active:scale-95 transition-all"
            title="Retour"
            style={{ backgroundImage: 'none', backgroundColor: '#f1f5f9' }}
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
        )}
        {fromInterventions && (
          <button
            onClick={() => history.push('/interventions')}
            className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center active:scale-95 transition-all"
            title="Retour"
            style={{ backgroundImage: 'none', backgroundColor: '#f1f5f9' }}
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
        )}
        <div>
          <h2 className="text-base font-black text-slate-900 tracking-tight leading-none mt-0 mb-1">Paramètres généraux</h2>
          <p className="text-sm text-slate-400 font-bold uppercase tracking-widest leading-none">Compte & Préférences</p>
        </div>
      </header>

      {/* ── ZONE SCROLLABLE ── */}
      <div className="flex-1 overflow-y-auto overscroll-contain">

        {/* ══════════════════════════════════════
            LAYOUT MOBILE / TABLETTE — inchangé
            ══════════════════════════════════════ */}
        <div className="lg:hidden p-5 pb-24 space-y-5">

          {/* Profile Card */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-3xl p-6 shadow-xl shadow-blue-100 flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white bg-opacity-20 backdrop-blur-md flex items-center justify-center text-xl font-black shadow-inner border border-white border-opacity-30">
              {form.avatar
                ? <img src={form.avatar} alt="avatar" className="w-full h-full object-cover rounded-2xl" />
                : initials}
            </div>
            <div>
              <p className="text-sm uppercase tracking-widest font-bold mb-1 text-blue-100">Compte Actif</p>
              <h3 className="text-lg font-black tracking-tight uppercase leading-none mt-0 mb-1.5">{fullName}</h3>
              <span
                className="inline-block mt-2 px-2.5 py-0.5 rounded-full font-extrabold uppercase border border-white border-opacity-50 tracking-wider"
                style={{ fontSize: '8.5px', backgroundColor: 'rgba(255,255,255,0.15)' }}
              >
                @{form.username || '—'}
              </span>
            </div>
          </div>

          {/* Section Identifiants */}
          <div className="bg-white rounded-3xl border border-slate-200 p-5 shadow-sm space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-slate-50">
              <UserIcon className="w-4 h-4 text-blue-600" />
              <h4 className="text-sm my-1 font-extrabold uppercase tracking-wider text-slate-700">Identifiants</h4>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-400 font-extrabold uppercase tracking-widest block mb-1">Nom</label>
                <input type="text" value={form.lastname} onChange={handleField('lastname')} className="browser-default bg-slate-50 border border-slate-200 rounded-2xl py-4 px-4 text-sm font-semibold focus:border-blue-500 outline-none w-full" style={{ outline: 'none' }} />
              </div>
              <div>
                <label className="text-xs text-slate-400 font-extrabold uppercase tracking-widest block mb-1">Prénom</label>
                <input type="text" value={form.firstname} onChange={handleField('firstname')} className="browser-default bg-slate-50 border border-slate-200 rounded-2xl py-4 px-4 text-sm font-semibold focus:border-blue-500 outline-none w-full" style={{ outline: 'none' }} />
              </div>
              <div>
                <label className="text-xs text-slate-400 font-extrabold uppercase tracking-widest block mb-1">E-mail</label>
                <input type="text" value={form.email} onChange={handleField('email')} className="browser-default bg-slate-50 border border-slate-200 rounded-2xl py-4 px-4 text-sm font-semibold focus:border-blue-500 outline-none w-full" style={{ outline: 'none' }} />
              </div>
              <div>
                <label className="text-xs text-slate-400 font-extrabold uppercase tracking-widest block mb-1">Identifiant</label>
                <input type="text" value={form.username} onChange={handleField('username')} className="browser-default bg-slate-50 border border-slate-200 rounded-2xl py-4 px-4 text-sm font-semibold focus:border-blue-500 outline-none w-full" style={{ outline: 'none' }} />
              </div>
            </div>
          </div>

          {/* Section Mot de passe */}
          <div className="bg-white rounded-3xl border border-slate-200 p-5 shadow-sm space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-slate-50">
              <LockClosedIcon className="w-4 h-4 text-blue-600" />
              <h4 className="text-sm my-1 font-extrabold uppercase tracking-wider text-slate-700">Mot de passe</h4>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-400 font-extrabold uppercase tracking-widest block mb-1">Mot de passe actuel <span className="text-red-400">*</span></label>
                <input type="password" value={form.password} onChange={handleField('password')} placeholder="Requis pour enregistrer" className="browser-default bg-slate-50 border border-slate-200 rounded-2xl py-4 px-4 text-sm font-semibold focus:border-blue-500 outline-none w-full" style={{ outline: 'none' }} />
              </div>
              <div>
                <label className="text-xs text-slate-400 font-extrabold uppercase tracking-widest block mb-1">Nouveau mot de passe</label>
                <input type="password" value={form.newpassword} onChange={handleField('newpassword')} placeholder="Laisser vide pour ne pas modifier" className="browser-default bg-slate-50 border border-slate-200 rounded-2xl py-4 px-4 text-sm font-semibold focus:border-blue-500 outline-none w-full" style={{ outline: 'none' }} />
              </div>
              <div>
                <label className="text-xs text-slate-400 font-extrabold uppercase tracking-widest block mb-1">Confirmation du mot de passe</label>
                <input type="password" value={form.confirm} onChange={handleField('confirm')} placeholder="Répéter le nouveau mot de passe" className="browser-default bg-slate-50 border border-slate-200 rounded-2xl py-4 px-4 text-sm font-semibold focus:border-blue-500 outline-none w-full" style={{ outline: 'none' }} />
              </div>
            </div>
          </div>

          {/* Section Avatar */}
          <div className="bg-white rounded-3xl border border-slate-200 p-5 shadow-sm space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-slate-50">
              <PhotographIcon className="w-4 h-4 text-blue-600" />
              <h4 className="text-sm my-1 font-extrabold uppercase tracking-wider text-slate-700">Avatar</h4>
            </div>
            <div className="flex items-center gap-4">
              {form.avatar
                ? <img src={form.avatar} alt="avatar" className="w-14 h-14 rounded-2xl object-cover border border-slate-200 shrink-0" />
                : (
                  <div className="w-14 h-14 rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
                    <PhotographIcon className="w-6 h-6 text-slate-400" />
                  </div>
                )
              }
              <label className="flex-1 py-3 px-4 bg-slate-50 border border-slate-200 hover:border-blue-400 hover:bg-blue-50 rounded-2xl text-sm font-semibold text-slate-600 flex items-center justify-center gap-2 cursor-pointer transition-all" style={{ userSelect: 'none' }}>
                <PhotographIcon className="w-4 h-4 text-slate-400" />
                <span>{form.avatar ? 'Changer la photo' : 'Choisir une photo'}</span>
                <input type="file" accept="image/*" onChange={handleAvatar} className="hidden" />
              </label>
            </div>
          </div>

          {/* Bouton Mettre à jour */}
          <button
            type="button"
            onClick={handleSettings}
            className="w-full py-4 bg-slate-900 hover:bg-slate-800 active:scale-95 text-white text-sm uppercase font-black tracking-widest rounded-2xl transition-all"
            style={{ backgroundImage: 'none', backgroundColor: '#0f172a' }}
          >
            Mettre à jour
          </button>

          {/* Feedback */}
          {success && (
            <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-2xl flex items-center gap-3 text-emerald-800 text-sm font-bold shadow-sm">
              <CheckCircleIcon className="w-5 h-5 text-emerald-600 shrink-0" />
              <span>{success}</span>
            </div>
          )}
          {error && (
            <div className="p-3 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-3 text-red-800 text-sm font-bold shadow-sm">
              <ExclamationCircleIcon className="w-5 h-5 text-red-500 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Notifications push (cet appareil) — surface principale des agents terrain */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <p className="text-sm font-black text-slate-800 flex items-center gap-2 my-0 mb-2">
              <BellIcon className="w-4 h-4 text-blue-500 flex-shrink-0" />
              Notifications
            </p>
            <PushSubscriptionToggle userId={user && user._id ? String(user._id) : null} />
          </div>

          {/* Bouton Se déconnecter */}
          <div className="pt-2">
            <button
              type="button"
              onClick={() => setShowLogoutConfirm(true)}
              className="w-full py-4 bg-red-50 text-red-600 hover:bg-red-100 transition-colors border-2 border-dashed border-red-200 rounded-2xl text-sm font-black uppercase tracking-widest flex items-center justify-center gap-2"
              style={{ backgroundImage: 'none', backgroundColor: '#fef2f2' }}
            >
              <LogoutIcon className="w-4 h-4" />
              <span>Se déconnecter</span>
            </button>
          </div>
        </div>

        {/* ══════════════════════════════════════
            LAYOUT DESKTOP — ConfigWebapp style
            ══════════════════════════════════════ */}
        <div className="hidden lg:block" style={{ paddingTop: '54px', paddingBottom: '2rem', paddingLeft: '77px', paddingRight: '3rem' }}>

          {/* Breadcrumb */}
          <div className="mb-5 flex items-center gap-3">
            {sidebarCollapsed && onToggleSidebar && (
              <button
                onClick={onToggleSidebar}
                className="flex w-8 h-8 rounded-lg bg-gray-800 hover:bg-gray-700 text-white items-center justify-center transition-colors shrink-0"
                title="Ouvrir le menu"
                style={{ backgroundImage: 'none', backgroundColor: '#1f2937' }}
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            )}
            <ol className="flex items-center gap-1 text-sm">
              <li className="flex items-center">
                <Link to="/" className="text-gray-400 hover:text-gray-600 transition-colors">
                  <HomeIcon className="flex-shrink-0 h-4 w-4" aria-hidden="true" />
                  <span className="sr-only">Accueil</span>
                </Link>
              </li>
              <li className="flex items-center">
                <span className="text-gray-300 mx-1 select-none">/</span>
                <span className="text-gray-400">Paramètres du compte</span>
              </li>
            </ol>
          </div>

          {fromInterventions && (
            <button
              onClick={() => history.push('/interventions')}
              className="flex items-center gap-1.5 text-slate-500 hover:text-blue-600 text-sm font-medium mb-4 transition-colors"
              style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Retour aux interventions
            </button>
          )}

          {/* Page header */}
          <div className="flex items-start justify-between mb-8">
            <div>
              <h1
                style={{ fontFamily: "'Fraunces', Georgia, serif", letterSpacing: '-0.02em', margin: 0 }}
                className="text-4xl font-bold text-gray-900 leading-none"
              >
                Paramètres du compte
              </h1>
              <p className="text-sm text-gray-400" style={{ margin: '0.5rem 0 0' }}>
                Gérez vos informations personnelles et votre mot de passe
              </p>
              {success && (
                <motion.p
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-1 text-xs text-emerald-600 font-medium mt-1"
                >
                  <CheckCircleIcon className="w-3.5 h-3.5" />
                  {success}
                </motion.p>
              )}
              {error && (
                <p className="text-xs text-red-500 font-medium mt-1">{error}</p>
              )}
            </div>
            <button
              type="button"
              onClick={handleSettings}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-bold rounded-xl shadow-sm transition-colors"
              style={{ backgroundImage: 'none', backgroundColor: '#2563eb' }}
            >
              <SaveIcon className="w-4 h-4" />
              Enregistrer
            </button>
          </div>

          {/* Grille 2 colonnes */}
          <div className="grid gap-6" style={{ gridTemplateColumns: '1fr 1fr' }}>

            {/* ── Colonne gauche ── */}
            <div className="space-y-6">

              {/* Card Identifiants */}
              <section className={cardClass}>
                <div className={cardHeaderClass}>
                  <UserIcon className="w-4 h-4 text-blue-500 mr-3 flex-shrink-0" />
                  <h2 className="text-sm font-bold text-slate-900 uppercase tracking-tight" style={{ margin: 0 }}>Identifiants</h2>
                </div>
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <label style={{ margin: 0 }}>
                      <span className={labelClass}>Nom</span>
                      <input type="text" value={form.lastname} onChange={handleField('lastname')} className={inputClass} />
                    </label>
                    <label style={{ margin: 0 }}>
                      <span className={labelClass}>Prénom</span>
                      <input type="text" value={form.firstname} onChange={handleField('firstname')} className={inputClass} />
                    </label>
                  </div>
                  <label style={{ margin: 0 }}>
                    <span className={labelClass}>Adresse e-mail</span>
                    <input type="text" value={form.email} onChange={handleField('email')} className={inputClass} />
                  </label>
                  <label style={{ margin: 0 }}>
                    <span className={labelClass}>Identifiant de connexion</span>
                    <input type="text" value={form.username} onChange={handleField('username')} className={inputClass} />
                  </label>
                </div>
              </section>

              {/* Card Avatar */}
              <section className={cardClass}>
                <div className={cardHeaderClass}>
                  <PhotographIcon className="w-4 h-4 text-blue-500 mr-3 flex-shrink-0" />
                  <h2 className="text-sm font-bold text-slate-900 uppercase tracking-tight" style={{ margin: 0 }}>Photo de profil</h2>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-5">
                    <div className="shrink-0">
                      {form.avatar
                        ? <img src={form.avatar} alt="avatar" className="w-16 h-16 rounded-xl object-cover border border-slate-200" />
                        : (
                          <div className="w-16 h-16 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center">
                            <PhotographIcon className="w-7 h-7 text-slate-400" />
                          </div>
                        )
                      }
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-slate-500 mb-2">JPG ou PNG - max 2 Mo recommandé</p>
                      <label className="inline-flex items-center gap-2 px-4 py-2 bg-slate-50 border border-slate-200 hover:border-blue-400 hover:bg-blue-50 rounded-lg text-sm font-semibold text-slate-600 cursor-pointer transition-all" style={{ userSelect: 'none' }}>
                        <PhotographIcon className="w-4 h-4 text-slate-400" />
                        <span>{form.avatar ? 'Changer la photo' : 'Choisir une photo'}</span>
                        <input type="file" accept="image/*" onChange={handleAvatar} className="hidden" />
                      </label>
                    </div>
                  </div>
                </div>
              </section>

            </div>

            {/* ── Colonne droite ── */}
            <div className="space-y-6">

              {/* Card Mot de passe */}
              <section className={cardClass}>
                <div className={cardHeaderClass}>
                  <LockClosedIcon className="w-4 h-4 text-blue-500 mr-3 flex-shrink-0" />
                  <h2 className="text-sm font-bold text-slate-900 uppercase tracking-tight" style={{ margin: 0 }}>Mot de passe</h2>
                </div>
                <div className="p-6 space-y-4">
                  <label style={{ margin: 0 }}>
                    <span className={labelClass}>Mot de passe actuel <span className="text-red-400 not-italic">*</span></span>
                    <input type="password" value={form.password} onChange={handleField('password')} placeholder="Requis pour enregistrer" className={inputClass} />
                  </label>
                  <label style={{ margin: 0 }}>
                    <span className={labelClass}>Nouveau mot de passe</span>
                    <input type="password" value={form.newpassword} onChange={handleField('newpassword')} placeholder="Laisser vide pour ne pas modifier" className={inputClass} />
                  </label>
                  <label style={{ margin: 0 }}>
                    <span className={labelClass}>Confirmation</span>
                    <input type="password" value={form.confirm} onChange={handleField('confirm')} placeholder="Répéter le nouveau mot de passe" className={inputClass} />
                  </label>
                </div>
              </section>

              {/* Notifications push (cet appareil) */}
              <section className={cardClass}>
                <div className={cardHeaderClass}>
                  <BellIcon className="w-4 h-4 text-blue-500 mr-3 flex-shrink-0" />
                  <h2 className="text-sm font-bold text-slate-900 uppercase tracking-tight" style={{ margin: 0 }}>Notifications</h2>
                </div>
                <div className="p-6">
                  <PushSubscriptionToggle userId={user && user._id ? String(user._id) : null} />
                </div>
              </section>

              {/* Déconnexion */}
              <section className={cardClass}>
                <div className={cardHeaderClass}>
                  <LogoutIcon className="w-4 h-4 text-red-400 mr-3 flex-shrink-0" />
                  <h2 className="text-sm font-bold text-slate-900 uppercase tracking-tight" style={{ margin: 0 }}>Session</h2>
                </div>
                <div className="p-6">
                  <p className="text-sm text-slate-500 mb-4">
                    Vous serez déconnecté de {establishment || 'votre établissement'} et devrez saisir à nouveau vos identifiants.
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowLogoutConfirm(true)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-50 border border-red-200 hover:bg-red-100 text-red-600 text-sm font-bold rounded-lg transition-colors"
                    style={{ backgroundImage: 'none', backgroundColor: '#fef2f2' }}
                  >
                    <LogoutIcon className="w-4 h-4" />
                    Se déconnecter
                  </button>
                </div>
              </section>

            </div>
          </div>
        </div>

      </div>

      {/* ── MODAL CONFIRMATION LOGOUT (partagée) ── */}
      {showLogoutConfirm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-6"
          style={{ backgroundColor: 'rgba(15,23,42,0.75)', backdropFilter: 'blur(4px)' }}
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0, y: 16 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.92, opacity: 0, y: 16 }}
            transition={{ type: 'spring', damping: 28, stiffness: 360 }}
            className="bg-white rounded-3xl p-6 w-full max-w-sm border border-slate-100 shadow-2xl space-y-5 text-center"
          >
            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto border border-red-200">
              <LogoutIcon className="w-6 h-6 text-red-500 animate-pulse" />
            </div>
            <div className="space-y-1">
              <h3 className="text-sm font-black text-slate-900">Confirmer la déconnexion ?</h3>
              <p className="text-sm text-slate-500 font-medium">
                Vous devrez saisir de nouveau vos identifiants pour accéder à {establishment || 'Mon établissement'}.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setShowLogoutConfirm(false)}
                className="py-3 bg-indigo-50 hover:bg-indigo-100 active:scale-95 text-indigo-600 text-sm font-semibold rounded-2xl transition-all"
                style={{ backgroundImage: 'none', backgroundColor: '#eef2ff' }}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleLogout}
                className="py-3 bg-red-600 hover:bg-red-700 active:scale-95 text-white text-sm font-extrabold rounded-2xl transition-all"
                style={{ backgroundImage: 'none', backgroundColor: '#dc2626' }}
              >
                Déconnecter
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  )
}

export default User
